import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  Link2, 
  Users,
  RefreshCw,
  Eye,
  Calendar,
  Trash2
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import GroupTaskModal from '../components/GroupTaskModal';
import { formatDistanceToNow, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GroupAssignments = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [linkedTasks, setLinkedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchLinkedTasks = useCallback(async () => {
    try {
      const response = await fetch(`${API}/linked-tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLinkedTasks(data);
      }
    } catch (error) {
      console.error('Error fetching linked tasks:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchLinkedTasks();
      setLoading(false);
    };
    loadData();
  }, [fetchLinkedTasks]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchLinkedTasks();
    setIsRefreshing(false);
    toast.success('Refreshed!');
  };

  const handleDelete = async (linkId, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }
    
    setDeletingId(linkId);
    try {
      const response = await fetch(`${API}/linked-tasks/${linkId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Deleted successfully');
        await fetchLinkedTasks();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const handleGroupCreated = () => {
    fetchLinkedTasks();
    setShowGroupModal(false);
    toast.success('Group task created!');
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-lg border-b">
          <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <div className="pl-10 lg:pl-0">
              <h1 className="font-heading text-xl sm:text-2xl font-bold">Group Assignments</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {linkedTasks.length} group assignments
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="sm:hidden"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="hidden sm:flex"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button onClick={() => setShowGroupModal(true)} data-testid="new-group-task-btn" size="sm">
                <Link2 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Assign to Group</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {linkedTasks.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="flex flex-col items-center justify-center text-muted-foreground">
                  <Link2 className="w-12 h-12 mb-4 opacity-20" />
                  <p className="mb-2">No group task assignments yet</p>
                  <p className="text-sm mb-4">Assign tasks to groups for bulk task creation</p>
                  <Button variant="outline" onClick={() => setShowGroupModal(true)}>
                    <Link2 className="w-4 h-4 mr-2" />
                    Assign to Group
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {linkedTasks.map((group) => {
                const completedCount = group.tasks?.filter(t => t.status === 'completed').length || 0;
                const totalCount = group.tasks?.length || 0;
                const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

                return (
                  <Card key={group.link_id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="font-heading text-lg flex items-center gap-2">
                            {group.name}
                            <Badge variant="outline" className="ml-2">
                              <Users className="w-3 h-3 mr-1" />
                              {totalCount} assignees
                            </Badge>
                          </CardTitle>
                          <p className="text-sm text-muted-foreground mt-1">
                            Group: {group.group_name}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/tasks/${group.tasks?.[0]?.task_id}`)}
                            disabled={!group.tasks?.length}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(group.link_id, group.name)}
                            disabled={deletingId === group.link_id}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-linked-task-${group.link_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Progress */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Progress</span>
                          <span className="font-medium">{completedCount} of {totalCount} completed</span>
                        </div>
                        <Progress value={progress} className="h-2" />
                      </div>

                      {/* Assignee Status List */}
                      <ScrollArea className="h-[150px]">
                        <div className="space-y-2">
                          {group.tasks?.map((task) => (
                            <div
                              key={task.id}
                              className="flex items-center justify-between p-2 rounded-lg bg-accent/30 hover:bg-accent/50 cursor-pointer transition-colors"
                              onClick={() => navigate(`/tasks/${task.id}`)}
                            >
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${
                                  task.status === 'completed' ? 'bg-green-500' :
                                  task.status === 'in progress' ? 'bg-amber-500' : 'bg-purple-500'
                                }`} />
                                <span className="text-sm font-medium">{task.assigned_to_name}</span>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {task.status}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>

                      {/* Created Date */}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t">
                        <Calendar className="w-3 h-3" />
                        Created {formatDistanceToNow(parseISO(group.created_at), { addSuffix: true })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </main>

        {/* Group Task Modal */}
        <GroupTaskModal
          open={showGroupModal}
          onClose={() => setShowGroupModal(false)}
          onTaskCreated={handleGroupCreated}
        />
      </div>
    </Sidebar>
  );
};

export default GroupAssignments;
